import { useEffect } from 'react';
import { useAppStore } from '@/store';
import { cn } from '@/lib/utils';

function colorForRatio(ratio: number) {
  // 0% green → 60% yellow → 90% red
  if (ratio < 0.6) {
    // green to yellow-green
    const t = ratio / 0.6;
    const r = Math.round(74 + (210 - 74) * t);
    const g = Math.round(222 + (203 - 222) * t);
    return `rgb(${r},${g},94)`;
  }
  if (ratio < 0.9) {
    const t = (ratio - 0.6) / 0.3;
    const r = Math.round(210 + (244 - 210) * t);
    const g = Math.round(203 + (114 - 203) * t);
    return `rgb(${r},${g},34)`;
  }
  // red
  const t = Math.min(1, (ratio - 0.9) / 0.1);
  const r = Math.round(244 + (220 - 244) * t);
  const g = Math.round(114 + (38 - 114) * t);
  return `rgb(${r},${g},38)`;
}

function formatNum(n: number | null | undefined) {
  if (n == null) return '—';
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
}

export function TokenGauge() {
  const usage = useAppStore((s) => s.lastUsage);
  const contextSize = useAppStore((s) => s.contextSize);
  const fieldLabels = useAppStore((s) => s.fieldLabels);
  const fetchChatInfo = useAppStore((s) => s.fetchChatInfo);

  useEffect(() => {
    // Refresh on first mount; backend may have new field labels.
    if (!contextSize || Object.keys(fieldLabels).length === 0) fetchChatInfo();
    else fetchChatInfo(); // also keep labels fresh on every mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const used = usage?.total_tokens ?? null;
  const ratio = used && contextSize ? Math.min(1, used / contextSize) : 0;

  const r = 14;
  const cx = 18;
  const cy = 18;
  const circ = 2 * Math.PI * r;
  const dash = circ * ratio;

  const color = ratio === 0 ? '#3f3f46' : colorForRatio(ratio);

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-0.5 select-none shrink-0',
        'text-white/70'
      )}
      title={
        contextSize
          ? `Токени: ${used ?? 0} / ${contextSize}\n` +
            (usage ? `prompt: ${usage.prompt_tokens ?? '—'}, completion: ${usage.completion_tokens ?? '—'}` : '')
          : 'Очікую дані…'
      }
    >
      <div className="relative w-9 h-9">
        <svg width="36" height="36" viewBox="0 0 36 36" className="-rotate-90 absolute inset-0">
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
          {ratio > 0 && (
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={color}
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={`${dash} ${circ}`}
              style={{ transition: 'stroke-dasharray 400ms ease, stroke 400ms ease' }}
            />
          )}
        </svg>
        <div
          className="absolute inset-0 flex items-center justify-center text-[9px] font-semibold"
          style={{ color: ratio > 0 ? color : 'rgba(255,255,255,0.4)' }}
        >
          {ratio > 0 ? `${Math.round(ratio * 100)}%` : '—'}
        </div>
      </div>
      <div className="text-[9px] leading-tight text-white/40 text-center font-mono whitespace-nowrap">
        {formatNum(used)}
        <span className="text-white/20">/</span>
        {formatNum(contextSize)}
      </div>
    </div>
  );
}
